import { useTranslation } from "react-i18next";
import { Page, Text, View, Document, Link } from "@react-pdf/renderer";
import { format, parseISO } from "date-fns";
import { Footer, PageHeader, PatientDetails, Table } from "./index";
import { styles } from "../styles";

interface Props {
  patient: {
    first_name: string;
    last_name: string;
    birthday: string;
    address: string;
    phone_number: string;
    nhs_number: string;
    email: string;
    therapist: {
      first_name: string;
      last_name: string;
    };
  };
  bmi: string;
  differential_diagnosis: string;
  signed_at: string;
  kickoff_date: string;
  discharge_urgency: string;
  discharge_further_action: boolean;
  discharge_patient_f2f: boolean;
  discharge_self_management: boolean;
  discharge_gp_action: boolean;
  discharge_advanced_physio: boolean;
  discharge_physio_f2f: boolean;
  discharge_other_text: string;
  plan: string;
  subjective: string;
  red_flags: string;
  past_medical_history: string;
  medication: string;
  objective: string;
  inspection_v2: string;
  palpation_v2: string;
  neurology_v2: string;
  joint_functionality_v2: string;
  functional_tests_v2: string;
  treatment_programme: string;
  adherence_measure: string;
  status: string;
  goal_functionality: string;
  goal_functionality_achieved: string;
  goal_free_text: string;
  goal_free_text_achieved: string;
  goal_pain: string;
  goal_pain_achieved: string;
  discharge_note_to_patient: string;
  ukDischargeLetter: any;
  initialEQ5D: string;
  latestEQ5D: string;
  gp_address: string;
  hasAnsweredFollowUpHealthReport: boolean;
}

export const DischargeLetter: React.VFC<Props> = ({
  patient: {
    first_name,
    last_name,
    birthday,
    address,
    phone_number,
    nhs_number,
    email,
    therapist: { first_name: therapistFirstName, last_name: therapistLastName },
  },
  bmi,
  differential_diagnosis,
  signed_at,
  kickoff_date,
  discharge_urgency,
  discharge_further_action,
  discharge_patient_f2f,
  discharge_self_management,
  discharge_gp_action,
  discharge_advanced_physio,
  discharge_physio_f2f,
  discharge_other_text,
  plan,
  subjective,
  red_flags,
  past_medical_history,
  medication,
  objective,
  inspection_v2,
  palpation_v2,
  neurology_v2,
  joint_functionality_v2,
  functional_tests_v2,
  treatment_programme,
  adherence_measure,
  status,
  goal_free_text,
  goal_free_text_achieved,
  goal_functionality,
  goal_functionality_achieved,
  goal_pain,
  goal_pain_achieved,
  discharge_note_to_patient,
  ukDischargeLetter,
  initialEQ5D,
  latestEQ5D,
  gp_address,
  hasAnsweredFollowUpHealthReport,
}) => {
  const { t } = useTranslation();
  const patientName = `${first_name} ${last_name}`;

  const getDischargeUrgencyTranslation = (urgency: string) => {
    switch (urgency) {
      case "routine":
        return t("patients.uk_discharge_letter.discharge_urgency_options.routine");
      case "urgent":
        return t("patients.uk_discharge_letter.discharge_urgency_options.urgent");
      case "":
        return "";
    }
  };

  const getDischargeFurtherActionTranslation = (dischargeFurtherAction: boolean) => {
    return dischargeFurtherAction ? t("patients.uk_discharge_letter.discharge_further_action") : "";
  };

  const getGoalAchievedTranslation = (achieved: string) => {
    if (achieved === "achieved") {
      return t("patients.uk_discharge_letter.goal_achieved");
    } else if (achieved === "not_achieved") {
      return t("patients.uk_discharge_letter.goal_not_achieved");
    } else if (achieved === "partially_achieved") {
      return t("patients.uk_discharge_letter.goal_partially_achieved");
    }
    return "";
  };

  return (
    <Document>
      <Page size="A4" style={styles.page}>
        <PageHeader />

        <View style={{ alignItems: "flex-end", marginTop: "100px" }}>
          <View style={styles.row}>
            <Text style={styles.bold}>{t("patients.uk_discharge_letter.contact_at")}</Text>
            <Link src={`mailto:${t("patients.uk_discharge_letter.contact_link")}`}>
              {t("patients.uk_discharge_letter.contact_link")}
            </Link>
          </View>
          <View style={styles.row}>
            <Text style={styles.bold}>{t("patients.uk_discharge_letter.date")}</Text>
            <Text>{signed_at && format(parseISO(signed_at), "d/M/yyyy")}</Text>
          </View>
        </View>

        <PatientDetails
          full_name={patientName}
          birthday={birthday}
          address={address.split("\n").join(", ")}
          phone_number={phone_number}
          nhs_number={nhs_number}
          differential_diagnosis={differential_diagnosis}
        />

        {plan && (
          <View style={styles.section}>
            <Text style={styles.bold}>{t("patients.uk_discharge_letter.plan")}</Text>
            <Text style={discharge_urgency === "urgent" ? { ...styles.underline } : {}}>
              {getDischargeUrgencyTranslation(discharge_urgency)}
            </Text>
            <Text>{getDischargeFurtherActionTranslation(discharge_further_action)}</Text>
            {discharge_patient_f2f && <Text>{t("patients.uk_discharge_letter.discharge_patient_f2f")}</Text>}
            {discharge_self_management && <Text>{t("patients.uk_discharge_letter.discharge_self_management")}</Text>}
            {discharge_gp_action && <Text>{t("patients.uk_discharge_letter.discharge_gp_action")}</Text>}
            {discharge_advanced_physio && <Text>{t("patients.uk_discharge_letter.discharge_advanced_physio")}</Text>}
            {discharge_physio_f2f && <Text>{t("patients.uk_discharge_letter.discharge_physio_f2f")}</Text>}
            {discharge_other_text && (
              <Text>
                {t("patients.uk_discharge_letter.discharge_other")}: {discharge_other_text}
              </Text>
            )}
            <Text>{plan}</Text>
          </View>
        )}

        {subjective && (
          <View style={styles.section}>
            <Text style={styles.bold}>{t("patients.uk_discharge_letter.subjective")}</Text>
            {kickoff_date && (
              <Text>
                {t("patients.uk_discharge_letter.initial_call")}: {format(parseISO(kickoff_date), "d/M/yyyy")}
              </Text>
            )}
            <Text>{subjective}</Text>
          </View>
        )}
        {red_flags && (
          <View style={styles.section}>
            <Text style={styles.bold}>{t("patients.uk_discharge_letter.red_flags")}</Text>
            <Text>{red_flags}</Text>
          </View>
        )}
        {past_medical_history && (
          <View style={styles.section}>
            <Text style={styles.bold}>{t("patients.uk_discharge_letter.past_medical_history")}</Text>
            <Text>{past_medical_history}</Text>
          </View>
        )}

        {medication && (
          <View style={styles.section}>
            <Text style={styles.bold}>{t("patients.uk_discharge_letter.medication")}</Text>
            <Text>{medication}</Text>
          </View>
        )}

        {objective && (
          <View style={styles.section}>
            <Text style={styles.bold}>{t("patients.uk_discharge_letter.objective")}</Text>
            <Text>{objective}</Text>
          </View>
        )}

        {inspection_v2 && (
          <View style={styles.section}>
            <Text style={styles.bold}>Observation</Text>
            <Text>{inspection_v2}</Text>
          </View>
        )}

        {palpation_v2 && (
          <View style={styles.section}>
            <Text style={styles.bold}>Palpation</Text>
            <Text>{palpation_v2}</Text>
          </View>
        )}

        {neurology_v2 && (
          <View style={styles.section}>
            <Text style={styles.bold}>Neurology</Text>
            <Text>{neurology_v2}</Text>
          </View>
        )}

        {joint_functionality_v2 && (
          <View style={styles.section}>
            <Text style={styles.bold}>Joint function</Text>
            <Text>{joint_functionality_v2}</Text>
          </View>
        )}

        {functional_tests_v2 && (
          <View style={styles.section}>
            <Text style={styles.bold}>Functional tests</Text>
            <Text>{functional_tests_v2}</Text>
          </View>
        )}

        {treatment_programme && (
          <View style={styles.section}>
            <Text style={styles.bold}>{t("patients.uk_discharge_letter.treatment_programme")}</Text>
            <Text>{treatment_programme}</Text>
          </View>
        )}

        <View style={styles.section}>
          <Text style={styles.bold}>{t("patients.uk_discharge_letter.progression")}</Text>
          <Table
            initialPainRating={ukDischargeLetter?.initial_pain}
            latestPainRating={ukDischargeLetter?.pain_rating}
            initialFunctionScore={ukDischargeLetter?.initial_functionality}
            latestFunctionScore={ukDischargeLetter?.functionality}
            healthformScoreType={ukDischargeLetter?.healthform_score_type || ""}
            initialHealthformScore={ukDischargeLetter?.initial_healthform_score || ""}
            healthformScore={ukDischargeLetter?.healthform_score || ""}
            initialEQ5D={initialEQ5D}
            latestEQ5D={latestEQ5D}
            adherence={adherence_measure}
            bmi={bmi}
            hasAnsweredFollowUpHealthReport={hasAnsweredFollowUpHealthReport}
          />
        </View>

        {status && (
          <View style={styles.section} wrap={false}>
            <Text style={styles.bold}>{t("patients.uk_discharge_letter.management")}</Text>
            <Text>{status}</Text>
          </View>
        )}

        <View style={styles.section} wrap={false}>
          <Text style={styles.bold}>{t("patients.uk_discharge_letter.goals")}</Text>
          {goal_pain && (
            <View style={styles.row}>
              <Text>{t("patients.uk_discharge_letter.goal_pain")}</Text>
              <Text>{`${goal_pain}. `}</Text>
              <Text>{getGoalAchievedTranslation(goal_pain_achieved)}</Text>
            </View>
          )}
          {goal_functionality && (
            <View style={styles.row}>
              <Text>{t("patients.uk_discharge_letter.goal_functionality")}</Text>
              <Text>{`${goal_functionality}. `}</Text>
              <Text>{getGoalAchievedTranslation(goal_functionality_achieved)}</Text>
            </View>
          )}
          {goal_free_text && (
            <View style={styles.row}>
              <Text>
                <Text>{t("patients.uk_discharge_letter.goal_free_text")}</Text>
                <Text>{`${goal_free_text}. `}</Text>
                <Text>{getGoalAchievedTranslation(goal_free_text_achieved)}</Text>
              </Text>
            </View>
          )}
        </View>

        <View style={styles.section}>
          <Text>{discharge_note_to_patient}</Text>
        </View>

        <Footer
          gpAddress={gp_address}
          patientName={patientName}
          patientAddress={address}
          patientEmail={email}
          therapistFirstName={therapistFirstName}
          therapistLastName={therapistLastName}
        />
      </Page>
    </Document>
  );
};
